import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  CopyButton,
  Divider,
  Group,
  Menu,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AnalyticsChatGoogleLink,
  AnalyticsCredential,
  AnalyticsTargetChat,
} from "../../../api.ts";
import { adminErrorMessage, adminGet, adminSend } from "../../../api.ts";
import { notifyAdminError, notifyAdminSuccess } from "../../../lib/admin-notify.ts";
import { AdminForm, AdminFormActions, AdminFormModal } from "../../../components/AdminForm.tsx";
import {
  AdminFilterBar,
  AdminFilterStatus,
  AdminHelpTip,
  AdminPageHeader,
  AdminRiskBadge,
} from "../../../components/AdminPageChrome.tsx";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { TablePagination } from "../../../components/TablePagination.tsx";
import { useClientPagination } from "../../../hooks/useClientPagination.ts";
import { BusinessDateTime } from "../../../components/BusinessDateTime.tsx";
import {
  LANDING_CARD_BG_HINT,
  LANDING_PAGE_BG_HINT,
  LandingBgUploader,
} from "./AnalyticsBasicConfig.tsx";

const SHORTLINK_BASE = "https://tg.deioio.com/s/";

// 表宽下限；投放列靠 flex-wrap 按单元格宽度自动折行，不写死每行个数
const TABLE_MIN_WIDTH = 1220;

const PLATFORM_BADGE_COLOR = {
  facebook: "blue",
  tiktok: "violet",
  google: "yellow",
  sharechat: "orange",
} as const;

const PLATFORM_LABEL = {
  facebook: "Meta",
  tiktok: "TikTok",
  google: "Google",
  sharechat: "ShareChat",
} as const;

function credDisplayName(cred: AnalyticsCredential): string {
  const remark = cred.remark?.trim() ?? "";
  if (remark !== "") return remark;
  if (cred.platform === "google") return cred.googleOperatingCustomerId ?? cred.externalId;
  // 短 ID：末 6 位足够区分
  return cred.externalId.slice(-6);
}

/** 投放 badge / 凭据是否命中搜索关键字（备注、ID、平台名） */
function credMatchesKeyword(cred: AnalyticsCredential, keyword: string): boolean {
  if (keyword === "") return false;
  const platformLabel = PLATFORM_LABEL[cred.platform].toLowerCase();
  const name = credDisplayName(cred).toLowerCase();
  const remark = (cred.remark ?? "").toLowerCase();
  const externalId = cred.externalId.toLowerCase();
  const customerId = (cred.googleOperatingCustomerId ?? "").toLowerCase();
  return (
    remark.includes(keyword) ||
    name.includes(keyword) ||
    externalId.includes(keyword) ||
    customerId.includes(keyword) ||
    platformLabel.includes(keyword) ||
    cred.platform.toLowerCase().includes(keyword) ||
    `${platformLabel}·${name}`.includes(keyword)
  );
}

function buildPlacementUrls(
  chatToken: string,
  cred: AnalyticsCredential,
): ReadonlyArray<{ readonly key: string; readonly label: string; readonly url: string }> {
  const base = `${SHORTLINK_BASE}${chatToken}`;
  if (cred.platform === "facebook") {
    return [
      {
        key: "fb",
        label: "Meta 投放 URL",
        url: `${base}?ad_id={{ad.id}}&pixel=${cred.externalId}`,
      },
    ];
  }
  if (cred.platform === "tiktok") {
    return [
      {
        key: "tt",
        label: "TikTok 投放 URL",
        url: `${base}?ad_id=__AID__&ttclid=__CLICKID__&pixel=${cred.externalId}`,
      },
    ];
  }
  if (cred.platform === "sharechat") {
    return [
      {
        key: "sc",
        label: "ShareChat 投放 URL",
        url: `${base}?ad_id=%%AD_ID%%&clickId=%%DELIVERY_IDENTIFIER%%&campaignname=%%CAMPAIGN_NAME%%&gaid=%%ADVERTISER_IDENTIFIER%%&userId=%%PUBLISHER_IDENTIFIER%%&p=sharechat&advid=${encodeURIComponent(cred.externalId)}`,
      },
    ];
  }
  // google 走弹窗流程（选广告系列类型 + 广告 ID），不在此生成
  return [];
}

/** 凭据的 Operating Customer ID（10 位，无横线），用于 shortlink 自动 pin 凭据 */
function credCustomerId(cred: AnalyticsCredential): string | null {
  const raw = cred.googleOperatingCustomerId ?? (cred.externalId !== "" ? cred.externalId : null);
  if (raw === null) return null;
  const cleaned = raw.replace(/-/g, "");
  return cleaned === "" ? null : cleaned;
}

/**
 * Google 投放 URL：
 * - {ignore} 为官方要求（final URL 内纯跟踪参数标记），降低 Destination mismatch 误判面
 * - p=google 静态标记落地页模式：AdsBot 抓取时宏为空也命中（docs/23 §23.6）
 * - wbraid / gbraid 不是 ValueTrack 宏（iOS 场景由 Google auto-tagging 自动附加），不手写
 * - adIdPart：手输数字 ad_id（调用方先 encode）或 "{creative}" 宏
 */
function buildGoogleCopyUrl(
  chatToken: string,
  customerId: string | null,
  adIdPart: string,
): string {
  const cidQ = customerId !== null ? `&customer_id=${encodeURIComponent(customerId)}` : "";
  return `${SHORTLINK_BASE}${chatToken}?{ignore}ad_id=${adIdPart}&gclid={gclid}${cidQ}&p=google`;
}

function copyUrl(url: string, okMsg: string): void {
  void navigator.clipboard.writeText(url).then(
    () => {
      notifications.show({ color: "green", message: okMsg });
    },
    () => {
      notifications.show({ color: "red", message: "复制失败，请检查浏览器权限" });
    },
  );
}

/**
 * 紧凑投放 badge：点击复制该 chat 的投放 URL。
 * 投放备注在「平台凭据」页统一改，本表不重复备注按钮。
 * highlight / dimmed：搜索投放备注时突出命中项、淡化其它。
 */
const PlacementBadge = memo(function PlacementBadge(props: {
  readonly chatToken: string;
  readonly cred: AnalyticsCredential;
  readonly onGoogleCopy: (chatToken: string, cred: AnalyticsCredential) => void;
  readonly highlight?: boolean;
  readonly dimmed?: boolean;
}) {
  const { chatToken, cred, onGoogleCopy, highlight = false, dimmed = false } = props;
  const color = PLATFORM_BADGE_COLOR[cred.platform];
  const platformLabel = PLATFORM_LABEL[cred.platform];
  const name = credDisplayName(cred);
  const shortLabel = `${platformLabel}·${name}`;
  const idLine =
    cred.platform === "google"
      ? `Customer ${cred.googleOperatingCustomerId ?? "?"} / SA ${cred.googleSaEmail ?? "?"}`
      : cred.platform === "sharechat"
        ? `App Name ${cred.externalId}`
        : `${cred.platform === "facebook" ? "Pixel" : "Pixel Code"} ${cred.externalId}`;
  const remarkLine = cred.remark?.trim() ? `备注: ${cred.remark.trim()}` : null;
  const badgeVariant = highlight ? "filled" : "light";
  const badgeStyle = dimmed ? { opacity: 0.32 } : undefined;

  // Google：弹窗选广告系列类型 + 广告 ID，生成专属投放 URL
  if (cred.platform === "google") {
    return (
      <Tooltip
        label={
          <Stack gap={2}>
            <Text size="xs">点击生成投放 URL（选广告系列类型 + 广告 ID）</Text>
            <Text size="xs" c="dimmed">
              {idLine}
            </Text>
            {remarkLine !== null && (
              <Text size="xs" c="dimmed">
                {remarkLine}
              </Text>
            )}
          </Stack>
        }
        multiline
        withArrow
        openDelay={300}
      >
        <Badge
          component="button"
          type="button"
          color={color}
          size="sm"
          variant={badgeVariant}
          className="placement-badge"
          style={badgeStyle}
          onClick={() => onGoogleCopy(chatToken, cred)}
        >
          {shortLabel}
        </Badge>
      </Tooltip>
    );
  }

  const urls = buildPlacementUrls(chatToken, cred);
  const only = urls[0];
  if (only === undefined) return null;
  return (
    <Tooltip
      label={
        <Stack gap={2}>
          <Text size="xs">点击复制投放 URL</Text>
          <Text size="xs" c="dimmed">
            {idLine}
          </Text>
          {remarkLine !== null && (
            <Text size="xs" c="dimmed">
              {remarkLine}
            </Text>
          )}
        </Stack>
      }
      multiline
      withArrow
      openDelay={300}
    >
      <Badge
        component="button"
        type="button"
        color={color}
        size="sm"
        variant={badgeVariant}
        className="placement-badge"
        style={badgeStyle}
        onClick={() => copyUrl(only.url, `已复制 ${shortLabel}`)}
      >
        {shortLabel}
      </Badge>
    </Tooltip>
  );
});

// ============================================================
// Google 投放 URL 弹窗（按广告系列类型区分链接形态）
// ============================================================

type GoogleCampaignType = "pmax" | "search" | "display" | "video" | "other";

const GOOGLE_CAMPAIGN_TYPE_OPTIONS = [
  { value: "pmax", label: "Performance Max" },
  { value: "search", label: "搜索 Search" },
  { value: "display", label: "展示 Display" },
  { value: "video", label: "视频 Video" },
  { value: "other", label: "其他 / 不确定" },
];

/**
 * Search / Display / Video 支持 ValueTrack {creative} 宏（官方参数表），
 * 广告 ID 由 Google 点击时自动填充；PMax 官方兼容表明确 {creative} 不支持
 * （无广告 ID 宏），必须手输固定 ad_id。「其他」按手输处理（最稳）。
 */
const supportsCreativeMacro = (t: GoogleCampaignType): boolean =>
  t === "search" || t === "display" || t === "video";

function GoogleAdCopyModal(props: {
  readonly botId: string;
  readonly target: { chatToken: string; cred: AnalyticsCredential } | null;
  readonly onClose: () => void;
}) {
  const { botId, target, onClose } = props;
  const [campaignType, setCampaignType] = useState<GoogleCampaignType>("pmax");
  const [adId, setAdId] = useState("");

  useEffect(() => {
    if (target !== null) {
      setCampaignType("pmax");
      setAdId("");
    }
  }, [target]);

  const preRegister = useMutation({
    mutationFn: (id: string) =>
      adminSend("POST", `/analytics/${botId}/ads`, {
        platform: "google",
        adId: id,
        credentialId: target?.cred.id ?? null,
      }),
    onSuccess: (_data, id) => {
      notifyAdminSuccess(
        `广告 ${id} 已预录入，请到「广告」tab 补配 ctId（缺 ctId 转化上报会跳过）`,
      );
    },
    onError: (e) => notifyAdminError(e, "广告预录入失败"),
  });

  const macroMode = supportsCreativeMacro(campaignType);
  const trimmedAdId = adId.trim();
  const adIdValid = /^\d{6,20}$/.test(trimmedAdId);

  const handleCopy = () => {
    if (target === null) return;
    const customerId = credCustomerId(target.cred);
    if (macroMode) {
      copyUrl(
        buildGoogleCopyUrl(target.chatToken, customerId, "{creative}"),
        "已复制投放 URL（广告 ID 由 Google 自动填充）",
      );
      onClose();
      return;
    }
    if (!adIdValid) return;
    copyUrl(
      buildGoogleCopyUrl(target.chatToken, customerId, encodeURIComponent(trimmedAdId)),
      `已复制广告 ${trimmedAdId} 的投放 URL`,
    );
    preRegister.mutate(trimmedAdId);
    onClose();
  };

  return (
    <AdminFormModal
      title="生成 Google 投放 URL"
      opened={target !== null}
      onClose={onClose}
      size="md"
    >
      <Stack gap="md">
        <Select
          label="广告系列类型"
          data={GOOGLE_CAMPAIGN_TYPE_OPTIONS}
          value={campaignType}
          onChange={(v) => setCampaignType((v ?? "pmax") as GoogleCampaignType)}
          allowDeselect={false}
        />
        {macroMode ? (
          <Alert color="blue" variant="light">
            该类型支持 <Code>{"{creative}"}</Code> 宏，广告 ID 由 Google
            点击时自动填充，整个系列用同一条链接即可。投放后广告会自动出现在「广告」tab，
            记得给它补配 ctId（缺 ctId 转化上报会跳过）。
          </Alert>
        ) : (
          <>
            <Alert color="yellow" variant="light">
              该类型没有广告 ID 宏（PMax 官方兼容表 <Code>{"{creative}"}</Code> 不支持），
              需要手动输入广告 ID，一个广告一条链接。
            </Alert>
            <TextInput
              label="广告 ID"
              placeholder="817220099160"
              description={
                "在 Google Ads 打开该广告的编辑页，浏览器地址栏 URL 中 adId= 后的数字即广告 ID" +
                "（例如 .../ads/edit/video?...&adId=817220099160&...）。" +
                "注意不要用 campaignId（广告系列 ID）或 adGroupId（广告组 ID）。"
              }
              value={adId}
              onChange={(e) => setAdId(e.currentTarget.value)}
              error={trimmedAdId !== "" && !adIdValid ? "应为 6-20 位数字" : null}
              data-autofocus
            />
          </>
        )}
        <AdminFormActions>
          <Button variant="default" type="button" onClick={onClose}>
            取消
          </Button>
          <Button onClick={handleCopy} disabled={!macroMode && !adIdValid}>
            复制投放 URL
          </Button>
        </AdminFormActions>
      </Stack>
    </AdminFormModal>
  );
}

/** 本地输入 + debounce 上抛，避免每键重渲整表；列表工具栏用 size=xs、无 label */
function DebouncedSearchInput(props: {
  readonly placeholder: string;
  readonly disabled?: boolean;
  readonly onDebouncedChange: (value: string) => void;
  readonly width?: number | string;
}) {
  const { placeholder, disabled, onDebouncedChange, width = 240 } = props;
  const [value, setValue] = useState("");
  const [debounced] = useDebouncedValue(value, 250);
  useEffect(() => {
    onDebouncedChange(debounced);
  }, [debounced, onDebouncedChange]);
  return (
    <TextInput
      size="xs"
      placeholder={placeholder}
      value={value}
      onChange={(e) => setValue(e.currentTarget.value)}
      style={{ width }}
      disabled={disabled === true}
    />
  );
}

/** 备注 Modal 自持 draft，输入不重渲表格 */
/** 标签筛选：全部 / 未打标 / 具体标签名（string & {} 避免字面量被 string 吞并） */
type TagFilter = "all" | "untagged" | (string & {});
/** 归因健康快捷筛：全部 / 仅不可归因（公开 或 Bot 未进群） */
type AttributionFilter = "all" | "degraded";

function isPublicUnattributable(it: AnalyticsTargetChat): boolean {
  return it.isPublic === true || it.attributionHealth === "degraded_public";
}

function isBotNotAdmin(it: AnalyticsTargetChat): boolean {
  return it.attributionHealth === "bot_not_admin";
}

function isAttributionBroken(it: AnalyticsTargetChat): boolean {
  return isPublicUnattributable(it) || isBotNotAdmin(it);
}

function normalizeTagsDraft(tags: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const t = raw.trim().slice(0, 32);
    if (t === "") continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
    if (out.length >= 10) break;
  }
  return out;
}

function TagsEditModal(props: {
  readonly chat: AnalyticsTargetChat | null;
  readonly suggestions: readonly string[];
  readonly loading: boolean;
  readonly onClose: () => void;
  readonly onSave: (tags: string[]) => void;
}) {
  const { chat, suggestions, loading, onClose, onSave } = props;
  const [draft, setDraft] = useState<string[]>([]);
  useEffect(() => {
    if (chat === null) return;
    setDraft([...(chat.tags ?? [])]);
  }, [chat]);
  return (
    <AdminFormModal
      title={`标签 - ${chat?.title ?? chat?.chatId ?? ""}`}
      opened={chat !== null}
      onClose={onClose}
      size="sm"
    >
      <AdminForm
        onSubmit={(e) => {
          e.preventDefault();
          onSave(normalizeTagsDraft(draft));
        }}
      >
        <TagsInput
          label="标签"
          description="回车添加；最多 10 个，单标签 32 字。清空后保存即未打标。"
          placeholder="如 印度 / 测试 / FB 投放"
          value={draft}
          onChange={setDraft}
          data={[...suggestions]}
          maxTags={10}
          splitChars={[",", "，"]}
          data-autofocus
        />
        <AdminFormActions>
          <Button variant="default" type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" loading={loading}>
            保存
          </Button>
        </AdminFormActions>
      </AdminForm>
    </AdminFormModal>
  );
}

function RemarkEditModal(props: {
  readonly opened: boolean;
  readonly title: string;
  readonly subtitle: string;
  readonly initialRemark: string;
  readonly placeholder: string;
  readonly description?: string;
  readonly loading: boolean;
  readonly onClose: () => void;
  readonly onSave: (remark: string) => void;
}) {
  const {
    opened,
    title,
    subtitle,
    initialRemark,
    placeholder,
    description,
    loading,
    onClose,
    onSave,
  } = props;
  const [draft, setDraft] = useState(initialRemark);
  useEffect(() => {
    if (opened) setDraft(initialRemark);
  }, [opened, initialRemark]);
  return (
    <AdminFormModal title={title} opened={opened} onClose={onClose} size="sm">
      <AdminForm
        onSubmit={(e) => {
          e.preventDefault();
          onSave(draft);
        }}
      >
        {subtitle !== "" && (
          <Text size="sm" c="dimmed">
            {subtitle}
          </Text>
        )}
        <TextInput
          label="备注"
          description={description}
          placeholder={placeholder}
          value={draft}
          maxLength={64}
          onChange={(e) => setDraft(e.currentTarget.value)}
          data-autofocus
        />
        <AdminFormActions>
          <Button variant="default" type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" loading={loading}>
            保存
          </Button>
        </AdminFormActions>
      </AdminForm>
    </AdminFormModal>
  );
}

function LandingBgModal(props: {
  readonly chat: AnalyticsTargetChat | null;
  readonly loading: boolean;
  readonly onClose: () => void;
  readonly onSave: (values: { cardBgUrl: string | null; pageBgUrl: string | null }) => void;
}) {
  const { chat, loading, onClose, onSave } = props;
  const [cardBg, setCardBg] = useState<string | null>(null);
  const [pageBg, setPageBg] = useState<string | null>(null);

  useEffect(() => {
    if (chat === null) return;
    setCardBg(chat.landingCardBgUrl);
    setPageBg(chat.landingPageBgUrl);
  }, [chat]);

  return (
    <AdminFormModal
      title={`落地页背景 - ${chat?.title ?? chat?.chatId ?? ""}`}
      opened={chat !== null}
      onClose={onClose}
    >
      <Stack gap="md">
        <Alert color="gray">
          此处为该频道单独覆盖；清除后保存即回退继承 bot 默认背景（在「基础设置」配置）。
        </Alert>
        <LandingBgUploader
          label="卡片背景（替换白色卡片底）"
          hint={LANDING_CARD_BG_HINT}
          value={cardBg}
          onChange={setCardBg}
        />
        <Divider />
        <LandingBgUploader
          label="页面背景（全屏）"
          hint={LANDING_PAGE_BG_HINT}
          value={pageBg}
          onChange={setPageBg}
        />
        <AdminFormActions>
          <Button variant="default" type="button" onClick={onClose}>
            取消
          </Button>
          <Button
            loading={loading}
            onClick={() => onSave({ cardBgUrl: cardBg, pageBgUrl: pageBg })}
          >
            保存
          </Button>
        </AdminFormActions>
      </Stack>
    </AdminFormModal>
  );
}

// ============================================================
// 频道 Google 投放链接（per-ad 主路径：一个广告一条共享邀请链）
// ============================================================

function GoogleLinksModal(props: {
  readonly botId: string;
  readonly chat: AnalyticsTargetChat | null;
  readonly defaultCustomerId: string | null;
  readonly onClose: () => void;
}) {
  const { botId, chat, defaultCustomerId, onClose } = props;
  const qc = useQueryClient();

  const linksQuery = useQuery({
    queryKey: ["bot", botId, "analytics-chats", chat?.id, "google-links"],
    queryFn: () =>
      adminGet<{ chatToken: string; items: AnalyticsChatGoogleLink[] }>(
        `/analytics/${botId}/chats/${chat?.id ?? ""}/google-links`,
      ),
    enabled: chat !== null,
  });

  const prewarm = useMutation({
    mutationFn: (adId: string) =>
      adminSend("POST", `/analytics/${botId}/chats/${chat?.id ?? ""}/google-links/prewarm`, {
        adId,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已创建该广告的加群链接" });
      void qc.invalidateQueries({
        queryKey: ["bot", botId, "analytics-chats", chat?.id, "google-links"],
      });
    },
    onError: (e) => notifyAdminError(e),
  });

  const items = linksQuery.data?.items ?? [];

  return (
    <AdminFormModal
      title={`Google 投放链接 - ${chat?.title ?? chat?.chatId ?? ""}`}
      opened={chat !== null}
      onClose={onClose}
      size="xl"
    >
      <Stack gap="md">
        <Alert color="blue" variant="light">
          Google 走 per-ad 主路径：一个广告一条专属加群链接。先在「广告」tab 预录入广告（ad_id
          自定，需配 ctId），再在此复制该广告的投放 URL。 缺 ctId 的广告转化会跳过上报。
        </Alert>
        {linksQuery.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} h={36} radius="sm" />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Center py="lg">
            <Text size="sm" c="dimmed">
              暂无 Google 广告。请先在「广告」tab 预录入（platform 选 google，配 ctId）。
            </Text>
          </Center>
        ) : (
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>备注</Table.Th>
                <Table.Th>ad_id</Table.Th>
                <Table.Th>ctId</Table.Th>
                <Table.Th>加群链接</Table.Th>
                <Table.Th>投放 URL</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((row) => {
                const cid = (row.customerId ?? defaultCustomerId)?.replace(/-/g, "") ?? null;
                const placementUrl = buildGoogleCopyUrl(
                  linksQuery.data?.chatToken ?? chat?.chatToken ?? "",
                  cid === "" ? null : cid,
                  encodeURIComponent(row.adId),
                );
                return (
                  <Table.Tr key={row.id}>
                    <Table.Td style={{ maxWidth: 160 }}>
                      <Text size="sm" style={{ wordBreak: "break-word" }}>
                        {row.remark?.trim() !== "" && row.remark !== null ? row.remark : "-"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {row.adId}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {row.googleConversionActionId !== null &&
                      row.googleConversionActionId !== "" ? (
                        <Badge color="green" size="sm" variant="light">
                          已配
                        </Badge>
                      ) : (
                        <Tooltip label="未配 ctId：该广告转化上报会跳过。去「广告」tab 补配。">
                          <Badge color="yellow" size="sm" variant="filled">
                            缺 ctId
                          </Badge>
                        </Tooltip>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {row.inviteLink !== null ? (
                        <CopyButton value={row.inviteLink}>
                          {({ copied, copy }) => (
                            <Badge
                              component="button"
                              type="button"
                              color="green"
                              size="sm"
                              variant="light"
                              className="placement-badge"
                              onClick={copy}
                            >
                              {copied ? "已复制" : "已建"}
                            </Badge>
                          )}
                        </CopyButton>
                      ) : (
                        <Button
                          size="compact-xs"
                          variant="light"
                          loading={prewarm.isPending}
                          onClick={() => prewarm.mutate(row.adId)}
                        >
                          立即建链
                        </Button>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Button
                        size="compact-xs"
                        variant="default"
                        onClick={() => copyUrl(placementUrl, `已复制广告 ${row.adId} 的投放 URL`)}
                      >
                        复制
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </AdminFormModal>
  );
}

// ============================================================
// 频道/群聊
// ============================================================

export function TargetChatsTab(props: { readonly botId: string; readonly timezone: string }) {
  const { botId, timezone } = props;
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [poolTuningChat, setPoolTuningChat] = useState<AnalyticsTargetChat | null>(null);
  const [remarkChat, setRemarkChat] = useState<AnalyticsTargetChat | null>(null);
  const [tagsChat, setTagsChat] = useState<AnalyticsTargetChat | null>(null);
  const [tagFilter, setTagFilter] = useState<TagFilter>("all");
  const [attributionFilter, setAttributionFilter] = useState<AttributionFilter>("all");
  const [landingBgChat, setLandingBgChat] = useState<AnalyticsTargetChat | null>(null);
  const [googleLinksChat, setGoogleLinksChat] = useState<AnalyticsTargetChat | null>(null);
  const [googleCopyTarget, setGoogleCopyTarget] = useState<{
    chatToken: string;
    cred: AnalyticsCredential;
  } | null>(null);
  const onGoogleCopy = useCallback((chatToken: string, cred: AnalyticsCredential) => {
    setGoogleCopyTarget({ chatToken, cred });
  }, []);
  // 双 filter：频道 / 投放各自独立，debounce 后驱动表格；输入框 state 在 DebouncedSearchInput 内
  const [debouncedChatSearch, setDebouncedChatSearch] = useState("");
  const [debouncedPlacementSearch, setDebouncedPlacementSearch] = useState("");
  const [filterKey, setFilterKey] = useState(0);
  const onDebouncedChatSearch = useCallback((value: string) => {
    setDebouncedChatSearch(value);
  }, []);
  const onDebouncedPlacementSearch = useCallback((value: string) => {
    setDebouncedPlacementSearch(value);
  }, []);
  const clearFilters = useCallback(() => {
    setDebouncedChatSearch("");
    setDebouncedPlacementSearch("");
    setFilterKey((k) => k + 1);
    setTagFilter("all");
    setAttributionFilter("all");
  }, []);

  const form = useForm<{ chatId: string; chatType: "channel" | "supergroup" }>({
    initialValues: { chatId: "", chatType: "channel" },
    validate: {
      chatId: (v) => (v.trim().length === 0 ? "请填写 Chat ID" : null),
    },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "analytics-chats"],
    queryFn: () => adminGet<{ items: AnalyticsTargetChat[] }>(`/analytics/${botId}/chats`),
    refetchInterval: 60_000,
  });

  const credentials = useQuery({
    queryKey: ["bot", botId, "analytics-credentials"],
    queryFn: () => adminGet<{ items: AnalyticsCredential[] }>(`/analytics/${botId}/credentials`),
    staleTime: 30_000,
  });

  const create = useMutation({
    mutationFn: (v: { chatId: string; chatType: "channel" | "supergroup" }) =>
      adminSend("POST", `/analytics/${botId}/chats`, {
        chatId: Number(v.chatId),
        chatType: v.chatType,
      }),
    onSuccess: () => {
      notifyAdminSuccess("已添加频道/群聊。请确认 Bot 仍是管理员，否则无法归因。");
      setCreateOpen(false);
      form.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e, "添加失败"),
  });

  const toggle = useMutation({
    mutationFn: (args: { id: string; isEnabled: boolean }) =>
      adminSend("PATCH", `/analytics/${botId}/chats/${args.id}`, {
        isEnabled: args.isEnabled,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e),
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminSend("DELETE", `/analytics/${botId}/chats/${id}`),
    onSuccess: () => {
      notifyAdminSuccess("已删除频道/群聊");
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e, "删除失败"),
  });

  const syncTitle = useMutation({
    mutationFn: (id: string) =>
      adminSend<{
        metaTgTitle?: string | null;
        metaUsername?: string | null;
        isPublic?: boolean;
        attributionWarning?: string | null;
      }>("POST", `/analytics/${botId}/chats/${id}/sync`),
    onSuccess: (data) => {
      const warn = data.isPublic === true ? " 注意：频道为公开，可能无法稳定归因回传。" : "";
      notifications.show({
        color: data.isPublic === true ? "orange" : "green",
        message: `已同步群名 / 公开状态 / 简介 / 人数 / 头像（群名以 Telegram 现名为准）。${warn}`,
      });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e),
  });

  const poolTuning = useMutation({
    mutationFn: (args: {
      id: string;
      poolMinThreshold: number | null;
      reclaimTimeoutMin: number | null;
    }) =>
      adminSend("PATCH", `/analytics/${botId}/chats/${args.id}`, {
        poolMinThreshold: args.poolMinThreshold,
        reclaimTimeoutMin: args.reclaimTimeoutMin,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已更新池参数" });
      setPoolTuningChat(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e),
  });

  const saveChatRemark = useMutation({
    mutationFn: (args: { id: string; remark: string }) =>
      adminSend("PATCH", `/analytics/${botId}/chats/${args.id}`, {
        remark: args.remark.trim() === "" ? null : args.remark.trim().slice(0, 64),
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "群备注已保存" });
      setRemarkChat(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e),
  });

  const saveChatTags = useMutation({
    mutationFn: (args: { id: string; tags: string[] }) =>
      adminSend("PATCH", `/analytics/${botId}/chats/${args.id}`, {
        tags: normalizeTagsDraft(args.tags),
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "标签已保存" });
      setTagsChat(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e),
  });

  const saveLandingBg = useMutation({
    mutationFn: (args: { id: string; cardBgUrl: string | null; pageBgUrl: string | null }) =>
      adminSend("PATCH", `/analytics/${botId}/chats/${args.id}`, {
        landingCardBgUrl: args.cardBgUrl,
        landingPageBgUrl: args.pageBgUrl,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已保存该频道落地页背景" });
      setLandingBgChat(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e),
  });

  const rebuildFallback = useMutation({
    mutationFn: (id: string) =>
      adminSend("POST", `/analytics/${botId}/chats/${id}/rebuild-fallback`),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已补建共享链接" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-chats"] });
    },
    onError: (e) => notifyAdminError(e),
  });

  const openChatRemark = useCallback((row: AnalyticsTargetChat) => {
    setRemarkChat(row);
  }, []);

  // 投放 badge 按 platform 排序
  const orderedCreds = useMemo(() => {
    const credItems = credentials.data?.items ?? [];
    return [...credItems].sort((a, b) => {
      const order = { facebook: 0, tiktok: 1, google: 2, sharechat: 3 } as const;
      const ao = order[a.platform];
      const bo = order[b.platform];
      if (ao !== bo) return ao - bo;
      return (a.externalId ?? "").localeCompare(b.externalId ?? "");
    });
  }, [credentials.data?.items]);

  // Google 投放 URL 的默认 customer_id：ad 未 pin 凭据时用 bot 唯一 enabled google 凭据
  const defaultGoogleCustomerId = useMemo(() => {
    const googleCreds = (credentials.data?.items ?? []).filter(
      (cr) => cr.platform === "google" && cr.isEnabled,
    );
    if (googleCreds.length !== 1) return null;
    return googleCreds[0]?.googleOperatingCustomerId ?? null;
  }, [credentials.data?.items]);

  const allItems = list.data?.items ?? [];
  const attributionIssue = useMemo(() => {
    let publicCount = 0;
    let botNotAdminCount = 0;
    let total = 0;
    for (const it of allItems) {
      const pub = isPublicUnattributable(it);
      const kicked = isBotNotAdmin(it);
      if (!pub && !kicked) continue;
      total += 1;
      if (pub) publicCount += 1;
      if (kicked) botNotAdminCount += 1;
    }
    return { total, publicCount, botNotAdminCount };
  }, [allItems]);
  const unattributableCount = attributionIssue.total;
  const chatKeyword = debouncedChatSearch.trim().toLowerCase();
  const placementKeyword = debouncedPlacementSearch.trim().toLowerCase();
  const placementFilterActive = placementKeyword !== "";

  // 投放 filter：只筛凭据 badge（bot 级共享，每行集合相同）
  const matchedCredIds = useMemo(() => {
    if (!placementFilterActive) return null;
    const ids = new Set<string>();
    for (const cred of orderedCreds) {
      if (credMatchesKeyword(cred, placementKeyword)) ids.add(cred.id);
    }
    return ids;
  }, [orderedCreds, placementFilterActive, placementKeyword]);

  const matchedCredCount = matchedCredIds?.size ?? 0;
  const placementNoMatch = placementFilterActive && matchedCredCount === 0;

  // 投放 filter 开启时只展示命中 badge；关闭时展示全部
  const displayCreds = useMemo(() => {
    if (!placementFilterActive) return orderedCreds;
    if (matchedCredIds === null || matchedCredIds.size === 0) return [];
    return orderedCreds.filter((cred) => matchedCredIds.has(cred.id));
  }, [orderedCreds, matchedCredIds, placementFilterActive]);

  // 顶部 badge：全部 + 未打标 + 全量去重标签（按出现次数降序，同次数按名字）
  const tagSummary = useMemo(() => {
    const countBy = new Map<string, number>();
    let untagged = 0;
    for (const it of allItems) {
      const tags = it.tags ?? [];
      if (tags.length === 0) {
        untagged += 1;
        continue;
      }
      for (const t of tags) {
        const name = t.trim();
        if (name === "") continue;
        countBy.set(name, (countBy.get(name) ?? 0) + 1);
      }
    }
    const tags = [...countBy.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "zh"))
      .map(([name, count]) => ({ name, count }));
    return { total: allItems.length, untagged, tags };
  }, [allItems]);

  const tagSuggestions = useMemo(() => tagSummary.tags.map((t) => t.name), [tagSummary.tags]);

  // 筛选 tag 已从全局消失时回退全部，避免空列表死胡同
  useEffect(() => {
    if (tagFilter === "all" || tagFilter === "untagged") return;
    if (!tagSummary.tags.some((t) => t.name === tagFilter)) {
      setTagFilter("all");
    }
  }, [tagFilter, tagSummary.tags]);

  // 频道 filter 只收窄行；投放 filter 不收窄行（凭据 bot 级），只改 badge 列
  const filteredItems = useMemo(() => {
    let items = allItems;
    if (attributionFilter === "degraded") {
      items = items.filter(isAttributionBroken);
    }
    if (tagFilter === "untagged") {
      items = items.filter((it) => (it.tags ?? []).length === 0);
    } else if (tagFilter !== "all") {
      items = items.filter((it) => (it.tags ?? []).includes(tagFilter));
    }
    if (chatKeyword === "") return items;
    return items.filter(
      (it) =>
        String(it.chatId).toLowerCase().includes(chatKeyword) ||
        (it.title ?? "").toLowerCase().includes(chatKeyword) ||
        (it.metaTgTitle ?? "").toLowerCase().includes(chatKeyword) ||
        (it.metaUsername ?? "").toLowerCase().includes(chatKeyword) ||
        (it.remark ?? "").toLowerCase().includes(chatKeyword) ||
        (it.tags ?? []).some((t) => t.toLowerCase().includes(chatKeyword)),
    );
  }, [allItems, chatKeyword, tagFilter, attributionFilter]);

  const filtersActive =
    chatKeyword !== "" ||
    placementFilterActive ||
    tagFilter !== "all" ||
    attributionFilter !== "all";

  const {
    pageItems: pagedChats,
    page: chatsPage,
    setPage: setChatsPage,
    pageSize: chatsPageSize,
    total: chatsTotal,
    totalPages: chatsTotalPages,
  } = useClientPagination(
    filteredItems,
    `${debouncedChatSearch}|${debouncedPlacementSearch}|${tagFilter}|${attributionFilter}`,
  );

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <AdminPageHeader
          title="频道/群聊"
          description="投放 badge 复制 URL；备注在「平台凭据」。筛选：频道收窄行 · 投放过滤 badge · 标签/不可归因可组合。"
          risk={
            unattributableCount > 0 ? (
              <AdminRiskBadge label={`不可归因 ${unattributableCount}`} color="red">
                <Text size="sm" fw={600}>
                  广告转化可能无法回传
                </Text>
                <Text size="sm">当前 {unattributableCount} 个目标 chat 有归因问题（可重叠）：</Text>
                <Text size="sm">
                  公开频道 {attributionIssue.publicCount} 个：有 username，用户常直接加入、不走
                  join-request。
                </Text>
                <Text size="sm">
                  Bot 未进群 {attributionIssue.botNotAdminCount} 个：被踢或不是管理员，无法建邀请链
                  / 审批入群 / 回传。
                </Text>
                <Text size="xs" c="dimmed">
                  公开的取消公开链接或改回私有；未进群的把 Bot 拉回并设管理员（含邀请权限）。
                  然后在行内「更多」点「同步群信息」。
                </Text>
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  onClick={() => setAttributionFilter("degraded")}
                >
                  只看不可归因群组
                </Button>
              </AdminRiskBadge>
            ) : undefined
          }
          actions={
            <Group gap="xs">
              <AdminHelpTip label="Bot 需 can_invite_users。同步群信息会把群名更新为 Telegram 现名并刷新公开状态。投放备注在「平台凭据」统一修改。" />
              <Button onClick={() => setCreateOpen(true)}>添加频道/群聊</Button>
            </Group>
          }
        />
        {allItems.length > 0 && (
          <>
            <AdminFilterBar>
              <DebouncedSearchInput
                key={`chat-${filterKey}`}
                placeholder="搜索频道：ID / 群名 / 备注 / 标签"
                disabled={allItems.length === 0}
                onDebouncedChange={onDebouncedChatSearch}
                width={240}
              />
              <DebouncedSearchInput
                key={`placement-${filterKey}`}
                placeholder="搜索投放：备注 / Pixel / 平台"
                disabled={orderedCreds.length === 0}
                onDebouncedChange={onDebouncedPlacementSearch}
                width={240}
              />
              {filtersActive && (
                <Button variant="subtle" size="xs" onClick={clearFilters}>
                  清空筛选
                </Button>
              )}
              {chatKeyword !== "" && (
                <AdminFilterStatus>
                  频道 {filteredItems.length}/{allItems.length}
                </AdminFilterStatus>
              )}
              {placementFilterActive && (
                <AdminFilterStatus tone={placementNoMatch ? "warn" : "ok"}>
                  {placementNoMatch
                    ? `投放无匹配「${debouncedPlacementSearch}」`
                    : `投放命中 ${matchedCredCount}`}
                </AdminFilterStatus>
              )}
            </AdminFilterBar>
            <Group gap={6} mb="sm" wrap="wrap">
              <UnstyledButton
                onClick={() => {
                  setTagFilter("all");
                  setAttributionFilter("all");
                }}
                style={{ lineHeight: 1 }}
                aria-pressed={tagFilter === "all" && attributionFilter === "all"}
              >
                <Badge
                  size="sm"
                  variant={tagFilter === "all" && attributionFilter === "all" ? "filled" : "light"}
                  color={tagFilter === "all" && attributionFilter === "all" ? "blue" : "gray"}
                  style={{ cursor: "pointer" }}
                >
                  全部 {tagSummary.total}
                </Badge>
              </UnstyledButton>
              <UnstyledButton
                onClick={() => setAttributionFilter((v) => (v === "degraded" ? "all" : "degraded"))}
                style={{ lineHeight: 1 }}
                aria-pressed={attributionFilter === "degraded"}
                title="公开频道或 Bot 未进群，广告转化可能无法回传"
              >
                <Badge
                  size="sm"
                  variant={attributionFilter === "degraded" ? "filled" : "light"}
                  color={
                    attributionFilter === "degraded"
                      ? "red"
                      : unattributableCount > 0
                        ? "red"
                        : "gray"
                  }
                  style={{ cursor: "pointer" }}
                >
                  不可归因 {unattributableCount}
                </Badge>
              </UnstyledButton>
              <UnstyledButton
                onClick={() => setTagFilter("untagged")}
                style={{ lineHeight: 1 }}
                aria-pressed={tagFilter === "untagged"}
              >
                <Badge
                  size="sm"
                  variant={tagFilter === "untagged" ? "filled" : "light"}
                  color={tagFilter === "untagged" ? "orange" : "gray"}
                  style={{ cursor: "pointer" }}
                >
                  未打标 {tagSummary.untagged}
                </Badge>
              </UnstyledButton>
              {tagSummary.tags.map((t) => {
                const active = tagFilter === t.name;
                return (
                  <UnstyledButton
                    key={t.name}
                    onClick={() => setTagFilter(t.name)}
                    style={{ lineHeight: 1 }}
                    aria-pressed={active}
                  >
                    <Badge
                      size="sm"
                      variant={active ? "filled" : "light"}
                      color={active ? "teal" : "gray"}
                      style={{ cursor: "pointer" }}
                    >
                      {t.name} {t.count}
                    </Badge>
                  </UnstyledButton>
                );
              })}
            </Group>
            {attributionFilter === "degraded" && (
              <AdminFilterStatus tone="warn">
                仅显示不可归因群组（{filteredItems.length}）
              </AdminFilterStatus>
            )}
          </>
        )}
        {list.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : allItems.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Text size="md" fw={600} c="dimmed">
                暂无频道/群聊
              </Text>
              <Text size="sm" c="dimmed">
                添加频道或群聊后可开始追踪广告归因。
              </Text>
              <Button onClick={() => setCreateOpen(true)}>立即添加</Button>
            </Stack>
          </Center>
        ) : filteredItems.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Text size="md" fw={600} c="dimmed">
                无匹配频道
              </Text>
              <Text size="sm" c="dimmed">
                {attributionFilter === "degraded" && chatKeyword === "" && tagFilter === "all"
                  ? "当前没有不可归因群组（公开频道或 Bot 未进群）。"
                  : tagFilter !== "all" && chatKeyword === ""
                    ? "当前标签下无频道，可切换标签或清空筛选。"
                    : chatKeyword !== ""
                      ? `频道筛选「${debouncedChatSearch}」无匹配（Chat ID / 群名 / 群备注 / 标签 / username）。`
                      : "当前筛选无结果。"}
              </Text>
              <Group gap="xs">
                {attributionFilter === "degraded" && (
                  <Button variant="light" color="red" onClick={() => setAttributionFilter("all")}>
                    取消不可归因筛选
                  </Button>
                )}
                {tagFilter !== "all" && (
                  <Button variant="light" onClick={() => setTagFilter("all")}>
                    看全部标签
                  </Button>
                )}
                <Button variant="subtle" onClick={clearFilters}>
                  清空筛选
                </Button>
              </Group>
            </Stack>
          </Center>
        ) : (
          <>
            <AdminTable minWidth={TABLE_MIN_WIDTH} maxHeight="calc(100vh - 280px)">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 200, minWidth: 180 }}>Chat</Table.Th>
                  <Table.Th style={{ width: 132, minWidth: 120 }}>创建时间</Table.Th>
                  <Table.Th style={{ width: 88, minWidth: 80 }}>短链</Table.Th>
                  <Table.Th style={{ minWidth: 240 }}>
                    <Group gap={6} wrap="nowrap">
                      <Text span size="sm" fw={700}>
                        投放
                      </Text>
                      <AdminHelpTip label="点 badge 复制该群投放 URL。有备注时显示备注名；备注在「平台凭据」修改。" />
                    </Group>
                  </Table.Th>
                  <Table.Th style={{ width: 64, minWidth: 56 }}>兜底</Table.Th>
                  <Table.Th style={{ width: 52, minWidth: 48 }}>启用</Table.Th>
                  <Table.Th style={{ width: 200, minWidth: 180 }}>操作</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pagedChats.map((row) => {
                  const shortlink = `${SHORTLINK_BASE}${row.chatToken}`;
                  const chatRemark = row.remark?.trim() ?? "";
                  const chatTags = row.tags ?? [];
                  const tgTitle = row.metaTgTitle?.trim() ?? "";
                  const opsTitle = row.title?.trim() ?? "";
                  const showTgAlias = tgTitle !== "" && opsTitle !== "" && tgTitle !== opsTitle;
                  const isPublic = isPublicUnattributable(row);
                  const botNotAdmin = isBotNotAdmin(row);
                  const broken = isPublic || botNotAdmin;
                  return (
                    <Table.Tr
                      key={row.id}
                      style={broken ? { backgroundColor: "var(--mantine-color-red-0)" } : undefined}
                    >
                      <Table.Td style={{ width: 200, maxWidth: 220 }}>
                        <Stack gap={4}>
                          <Text size="sm" fw={500} style={{ wordBreak: "break-word" }}>
                            {row.title ?? "-"}
                          </Text>
                          {showTgAlias && (
                            <Text size="xs" c="dimmed">
                              TG: {tgTitle}
                            </Text>
                          )}
                          {isPublic && (
                            <Tooltip
                              label={
                                row.attributionWarning ??
                                "公开频道用户可能直接加入、不走 join-request，广告转化可能无法回传。取消公开后同步群信息。"
                              }
                              multiline
                              maw={320}
                              withArrow
                            >
                              <Badge color="red" size="sm" variant="filled">
                                {row.metaUsername ? `公开·@${row.metaUsername}` : "公开·不可用归因"}
                              </Badge>
                            </Tooltip>
                          )}
                          {botNotAdmin && (
                            <Tooltip
                              label="Bot 不在频道或不是管理员，无法建邀请链、审批入群和回传。把 Bot 拉回并设管理员（含邀请权限）后同步群信息。"
                              multiline
                              maw={320}
                              withArrow
                            >
                              <Badge color="orange" size="sm" variant="filled">
                                Bot未进群
                              </Badge>
                            </Tooltip>
                          )}
                          {chatRemark !== "" && (
                            <Badge size="sm" variant="light" color="grape" title={chatRemark}>
                              {chatRemark}
                            </Badge>
                          )}
                          {chatTags.length > 0 && (
                            <Group gap={4} wrap="wrap">
                              {chatTags.map((t) => (
                                <Badge
                                  key={t}
                                  size="xs"
                                  variant="outline"
                                  color="teal"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => setTagFilter(t)}
                                  title={`筛选标签：${t}`}
                                >
                                  {t}
                                </Badge>
                              ))}
                            </Group>
                          )}
                          <Group gap={6} wrap="wrap">
                            <Badge color={row.chatType === "channel" ? "blue" : "teal"} size="xs">
                              {row.chatType === "channel" ? "频道" : "超群"}
                            </Badge>
                            <Text size="xs" c="dimmed" ff="monospace">
                              {row.chatId}
                            </Text>
                          </Group>
                        </Stack>
                      </Table.Td>
                      <Table.Td style={{ width: 132, whiteSpace: "nowrap" }}>
                        <BusinessDateTime iso={row.createdAt} businessTimezone={timezone} />
                      </Table.Td>
                      <Table.Td>
                        <CopyButton value={shortlink}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "已复制" : shortlink}
                              withArrow
                              multiline
                              maw={360}
                            >
                              <Button size="compact-xs" variant="light" onClick={copy}>
                                {copied ? "已复制" : "复制短链"}
                              </Button>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Table.Td>
                      <Table.Td style={{ minWidth: 240, width: "36%", verticalAlign: "top" }}>
                        {orderedCreds.length === 0 ? (
                          <Text size="xs" c="dimmed">
                            先在「平台凭据」配置
                          </Text>
                        ) : placementNoMatch ? (
                          <Text size="xs" c="dimmed">
                            无匹配投放
                          </Text>
                        ) : displayCreds.length === 0 ? (
                          <Text size="xs" c="dimmed">
                            先在「平台凭据」配置
                          </Text>
                        ) : (
                          <div className="placement-badge-wrap">
                            {displayCreds.map((cred) => (
                              <PlacementBadge
                                key={cred.id}
                                chatToken={row.chatToken}
                                cred={cred}
                                onGoogleCopy={onGoogleCopy}
                                highlight={placementFilterActive}
                              />
                            ))}
                          </div>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {row.fallbackInviteLink != null ? (
                          <CopyButton value={row.fallbackInviteLink}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "已复制" : (row.fallbackInviteLink ?? "")}
                                withArrow
                                multiline
                                maw={320}
                              >
                                <Badge
                                  component="button"
                                  type="button"
                                  color="green"
                                  size="sm"
                                  variant="light"
                                  className="placement-badge"
                                  onClick={copy}
                                >
                                  {copied ? "已复制" : "已建"}
                                </Badge>
                              </Tooltip>
                            )}
                          </CopyButton>
                        ) : (
                          <Badge color="gray" size="sm" variant="light">
                            未建
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Switch
                          checked={row.isEnabled}
                          size="sm"
                          onChange={(e) =>
                            toggle.mutate({ id: row.id, isEnabled: e.currentTarget.checked })
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <Button
                            size="compact-xs"
                            variant="light"
                            color="grape"
                            onClick={() => openChatRemark(row)}
                          >
                            {chatRemark !== "" ? "改备注" : "群备注"}
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="light"
                            color="teal"
                            onClick={() => setTagsChat(row)}
                          >
                            {chatTags.length > 0 ? `标签(${chatTags.length})` : "标签"}
                          </Button>
                          <Menu shadow="md" width={160} position="bottom-end" withinPortal>
                            <Menu.Target>
                              <Button size="compact-xs" variant="default">
                                更多
                              </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={() => setTagsChat(row)}>分配标签</Menu.Item>
                              <Menu.Item onClick={() => setGoogleLinksChat(row)}>
                                Google 投放链接
                              </Menu.Item>
                              <Menu.Item onClick={() => setPoolTuningChat(row)}>池参数</Menu.Item>
                              <Menu.Item onClick={() => setLandingBgChat(row)}>
                                落地页背景
                              </Menu.Item>
                              <Menu.Item
                                onClick={() => {
                                  window.open(row.previewUrl, "_blank", "noopener");
                                }}
                              >
                                预览落地页
                              </Menu.Item>
                              <Menu.Item
                                disabled={rebuildFallback.isPending}
                                onClick={() => rebuildFallback.mutate(row.id)}
                              >
                                补建共享链接
                              </Menu.Item>
                              <Menu.Item
                                disabled={syncTitle.isPending}
                                onClick={() => syncTitle.mutate(row.id)}
                              >
                                同步群信息
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item
                                color="red"
                                onClick={() => {
                                  modals.openConfirmModal({
                                    title: "确认删除",
                                    children: `确认删除频道/群聊「${row.title ?? row.chatId}」？`,
                                    labels: { confirm: "删除", cancel: "取消" },
                                    confirmProps: { color: "red" },
                                    onConfirm: () => remove.mutate(row.id),
                                  });
                                }}
                              >
                                删除
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </AdminTable>
            <TablePagination
              page={chatsPage}
              pageSize={chatsPageSize}
              total={chatsTotal}
              totalPages={chatsTotalPages}
              onPageChange={setChatsPage}
            />
          </>
        )}
      </Card>

      <AdminFormModal
        title="添加频道/群聊"
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <AdminForm onSubmit={form.onSubmit((v) => create.mutate(v))}>
          <Alert color="yellow" title="先加 Bot 再填 Chat ID">
            请先将 Bot 加入频道/群聊并设为管理员（至少 can_invite_users），再添加。公开群且 Bot
            不是管理员时无法归因。
          </Alert>
          {create.isError ? (
            <Alert color="red" title="添加失败">
              <Text size="xs" className="admin-error-text">
                {adminErrorMessage(create.error)}
              </Text>
            </Alert>
          ) : null}
          <NumberInput
            label="Chat ID（负数）"
            description="频道/群组 ID，通常为负数。"
            required
            value={form.values.chatId}
            onChange={(v) => form.setFieldValue("chatId", String(v))}
            error={form.errors["chatId"]}
            hideControls
          />
          <Select
            label="Chat 类型"
            data={[
              { label: "频道 channel", value: "channel" },
              { label: "超级群组 supergroup", value: "supergroup" },
            ]}
            required
            {...form.getInputProps("chatType")}
          />
          <AdminFormActions>
            <Button variant="default" type="button" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button type="submit" loading={create.isPending}>
              校验并添加
            </Button>
          </AdminFormActions>
        </AdminForm>
      </AdminFormModal>

      <PoolTuningModal
        chat={poolTuningChat}
        loading={poolTuning.isPending}
        onClose={() => setPoolTuningChat(null)}
        onSave={(values) => {
          if (poolTuningChat === null) return;
          poolTuning.mutate({
            id: poolTuningChat.id,
            poolMinThreshold: values.poolMinThreshold,
            reclaimTimeoutMin: values.reclaimTimeoutMin,
          });
        }}
      />

      <RemarkEditModal
        opened={remarkChat !== null}
        title="群备注"
        subtitle={remarkChat !== null ? `${remarkChat.title ?? "-"} · ${remarkChat.chatId}` : ""}
        initialRemark={remarkChat?.remark ?? ""}
        placeholder="如 Arrow Sachin / Dinesh 主群"
        description="运营可见名称，最长 64 字；清空后保存即删除备注"
        loading={saveChatRemark.isPending}
        onClose={() => setRemarkChat(null)}
        onSave={(remark) => {
          if (remarkChat === null) return;
          saveChatRemark.mutate({ id: remarkChat.id, remark });
        }}
      />

      <TagsEditModal
        chat={tagsChat}
        suggestions={tagSuggestions}
        loading={saveChatTags.isPending}
        onClose={() => setTagsChat(null)}
        onSave={(tags) => {
          if (tagsChat === null) return;
          saveChatTags.mutate({ id: tagsChat.id, tags });
        }}
      />

      <GoogleLinksModal
        botId={botId}
        chat={googleLinksChat}
        defaultCustomerId={defaultGoogleCustomerId}
        onClose={() => setGoogleLinksChat(null)}
      />

      <GoogleAdCopyModal
        botId={botId}
        target={googleCopyTarget}
        onClose={() => setGoogleCopyTarget(null)}
      />

      <LandingBgModal
        chat={landingBgChat}
        loading={saveLandingBg.isPending}
        onClose={() => setLandingBgChat(null)}
        onSave={(values) => {
          if (landingBgChat === null) return;
          saveLandingBg.mutate({
            id: landingBgChat.id,
            cardBgUrl: values.cardBgUrl,
            pageBgUrl: values.pageBgUrl,
          });
        }}
      />
    </>
  );
}

function PoolTuningModal(props: {
  readonly chat: AnalyticsTargetChat | null;
  readonly loading: boolean;
  readonly onClose: () => void;
  readonly onSave: (values: {
    poolMinThreshold: number | null;
    reclaimTimeoutMin: number | null;
  }) => void;
}) {
  const { chat, loading, onClose, onSave } = props;
  const [poolMin, setPoolMin] = useState<string>("");
  const [reclaimMin, setReclaimMin] = useState<string>("");

  useEffect(() => {
    if (chat === null) return;
    setPoolMin(chat.poolMinThreshold === null ? "" : String(chat.poolMinThreshold));
    setReclaimMin(chat.reclaimTimeoutMin === null ? "" : String(chat.reclaimTimeoutMin));
  }, [chat]);

  const parseNullableInt = (raw: string): number | null => {
    const s = raw.trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  return (
    <AdminFormModal
      title={chat === null ? "池参数" : `池参数：${chat.title ?? chat.chatId}`}
      opened={chat !== null}
      onClose={onClose}
    >
      <AdminForm
        onSubmit={(e) => {
          e.preventDefault();
          onSave({
            poolMinThreshold: parseNullableInt(poolMin),
            reclaimTimeoutMin: parseNullableInt(reclaimMin),
          });
        }}
      >
        <Text size="sm" c="dimmed">
          留空则完全按 24h 访问量自动分档：默认 200 / 1 万+ 500 / 5 万+ 800 / 10 万+ 1000。
          手动填写仅作下限（自动档位更高时以自动为准）；回收分钟可加速但不能拖慢自动档位。
        </Text>
        <NumberInput
          label="池水位 pool_min_threshold（可选下限）"
          description="50-2000，留空=纯自动分档"
          value={poolMin}
          onChange={(v) => setPoolMin(String(v))}
          hideControls
          min={50}
          max={2000}
        />
        <NumberInput
          label="回收分钟 reclaim_timeout_min"
          description="1-30，assigned 超此分钟未入群则回收"
          value={reclaimMin}
          onChange={(v) => setReclaimMin(String(v))}
          hideControls
          min={1}
          max={30}
        />
        <AdminFormActions>
          <Button variant="default" type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" loading={loading}>
            保存
          </Button>
        </AdminFormActions>
      </AdminForm>
    </AdminFormModal>
  );
}
